import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { FaInfoCircle } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import Checkbox from '@/shared/components/input/Checkbox';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, CopyIcon, DeleteIcon, IconPencil } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';
import ToastrService from '../../../shared/services/ToastrService';
import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete, CanUpdate } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import dataCacheService from '@/shared/services/DataCacheService';
import TextSearch from '@/shared/components/input/TextSearch';

import messages from './InteractionSettingsMessages';
import { parse } from 'filepond';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';

class MailTemplateOverview extends Component {
  state = {
    templates: null,
    loading: true,
    companyId: null,
    deleteTemplate: null,
    search: '',
    context: undefined,
    languageCode: null,
    contexts: [],
    languages: [],
  };

  componentDidMount() {
    const { intl } = this.props;
    const companyId = dataCacheService.getDefaultCompany().id;
    const filters = JSON.parse(sessionStorage.getItem('MailTemplateSettingsParams')) || {};
    api.base.get(`${config.appUrls.api}/api/Template/Company`).then(({ data }) => {
      this.setState({
        templates: data.sort(dynamicSortMultiple('name', 'context')),
        loading: false,
        companyId,
        search: filters.search,
        languageCode: filters.languageCode,
        context: filters.context,
        contexts: [...new Set(data.map(p => p.context))].sort(),
        languages: [...new Set(data.flatMap(t => t.languages.map(l => l.languageCode)))].sort(
          x => x.name,
        ),
      });
    });
  }

  getFilteredTemplates = () => {
    const { templates, search, languageCode, context } = this.state;

    if (!search && !languageCode && context == undefined) return templates;
    let result = templates;
    if (search) {
      const parts = search.split(' ');
      parts.forEach(term => {
        result = result.filter(
          x => x.name && x.name.toLowerCase().indexOf(term.toLowerCase()) >= 0,
        );
      });
    }
    if (languageCode) {
      result = result.filter(x => x.languages.some(l => l.languageCode === languageCode));
    }
    if (context != undefined) {
      result = result.filter(x => x.context === context);
    }
    return result;
  };

  saveFilters = () => {
    const { context, search, languageCode } = this.state;
    sessionStorage.setItem(
      'MailTemplateSettingsParams',
      JSON.stringify({
        context: context,
        search: search,
        languageCode: languageCode,
      }),
    );
  };

  addMailTemplate = () => {
    const { stateService } = this.props;
    stateService.gotoState('settings.templates.new');
  };

  editMailTemplate = template => {
    const { stateService } = this.props;
    const { companyId } = this.state;
    stateService.gotoState('settings.templates.edit', {
      templateId: template.id,
      companyId: companyId,
    });
  };

  deleteTemplate = template => {
    if (!template || !template.id) return;
    api.base.delete(`${config.appUrls.api}/api/template/template/${template.id}`).then(
      () => {
        this.setState(({ templates }) => ({
          templates: templates.filter(t => t.id !== template.id),
          deleteTemplate: null,
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  copyTemplate = template => {
    if (!template || !template.id) return;
    api.base.post(`${config.appUrls.api}/api/template/copy/${template.id}`).then(
      ({ data }) => {
        const newTemplate = data;
        const { templates } = this.state;
        const { intl } = this.props;

        const sourceTemplateIndex = templates.findIndex(t => t.id === template.id);
        if (sourceTemplateIndex >= 0) {
          // Create a new array to hold the updated templates list
          const updatedTemplates = [
            ...templates.slice(0, sourceTemplateIndex + 1), // Copy elements before the source template (including the source template)
            newTemplate, // Add the copied template after the source template
            ...templates.slice(sourceTemplateIndex + 1), // Copy elements after the source template
          ];
          // Update state with the new templates array
          this.setState({
            templates: updatedTemplates,
          });
        }
        ToastrService.changesSaved(intl);
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  render() {
    const {
      templates,
      loading,
      deleteTemplate,
      search,
      languages,
      languageCode,
      contexts,
      context,
    } = this.state;
    const { intl, stateService, tab } = this.props;

    const canUpdateTemplates = authService.hasPermission({
      functionality: ['Settings', 'EmailAndInteractionTemplates'],
      privilege: privileges.Update,
    });

    const filteredTemplates = this.getFilteredTemplates();

    return (
      <div className="panel">
        <div className="panel-heading">
          <CanCreate on={['Settings', 'EmailAndInteractionTemplates']}>
            <div className="panel-buttons">
              <button
                type="button"
                className="btn btn-inverse"
                onClick={() => this.addMailTemplate()}
              >
                <i className="fa fa-plus" /> <FormattedMessage {...messages.Add} />
              </button>
            </div>
          </CanCreate>
        </div>
        {!loading && templates ? (
          <div className="panel-body container-fluid">
            <div className="row">
              <div className="col-md-4">
                <TextSearch
                  className="form-control"
                  value={search}
                  placeholder={intl.formatMessage(messages.SearchName)}
                  onChange={value => {
                    this.setState({ search: value || '' });
                    this.saveFilters();
                  }}
                  delay={200}
                />
              </div>
              <div className="col-md-4">
                <select
                  id="ContextFilter"
                  name="ContextFilter"
                  className="form-control"
                  onChange={e => {
                    const newContext = parseInt(e.target.value);
                    this.setState(
                      {
                        context: isNaN(newContext) ? undefined : newContext,
                      },
                      () => {
                        this.saveFilters();
                      },
                    );
                  }}
                  value={context == undefined ? '' : context}
                >
                  <option value="">{intl.formatMessage(messages.TemplateContext)}</option>
                  {contexts &&
                    contexts.map(t => (
                      <option key={t} value={t}>
                        {intl.formatMessage(listItemMsg('templateContext', t))}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-4">
                <select
                  id="LanguageFilter"
                  name="LanguageFilter"
                  className="form-control"
                  onChange={e => {
                    this.setState(
                      {
                        languageCode: e.target.value || null,
                      },
                      () => {
                        this.saveFilters();
                      },
                    );
                  }}
                  value={languageCode || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllLanguages)}</option>
                  {languages &&
                    languages.map(t => (
                      <option key={t} value={t}>
                        {intl.formatMessage(listItemMsg('language', t))}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <table className="table table-grid table-hover margin5T">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage {...messages.TemplateName} />
                  </th>
                  <th>
                    <FormattedMessage {...messages.TemplateContext} />
                  </th>
                  <th>
                    <FormattedMessage {...messages.TemplateLanguages} />
                  </th>
                  <th className="text-right">
                    <FormattedMessage {...messages.Actions} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(template => (
                  <tr key={template.id}>
                    <td>
                      {intl.formatMessage(listItemMsg('template', template.id, template.name))}
                    </td>
                    <td>{intl.formatMessage(listItemMsg('templateContext', template.context))}</td>
                    <td>{template.languages.map(lang => lang.languageCode).join(', ')}</td>
                    <td className="text-right">
                      <a
                        onClick={() => this.editMailTemplate(template)}
                        className="btn btn-xs btn-icon"
                      >
                        <IconPencil />
                      </a>
                      <a
                        onClick={() => this.copyTemplate(template)}
                        className="btn btn-xs btn-icon"
                      >
                        <CopyIcon />
                      </a>
                      <CanDelete on={['Settings', 'EmailAndInteractionTemplates']}>
                        <a
                          onClick={() => this.setState({ deleteTemplate: template })}
                          className="btn btn-xs btn-icon"
                        >
                          <DeleteIcon />
                        </a>
                      </CanDelete>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {deleteTemplate !== null && (
              <YesNoModal
                intl={intl}
                show
                title={intl.formatMessage(messages.DeleteTemplate)}
                message={intl.formatMessage(messages.DeleteTemplateConfirmationMsg)}
                onNo={() =>
                  this.setState(state => ({
                    ...state,
                    deleteTemplate: null,
                  }))
                }
                onSave={() => this.deleteTemplate(deleteTemplate)}
              />
            )}
          </div>
        ) : null}
        {loading && <LoadingAnimation />}
      </div>
    );
  }
}

MailTemplateOverview.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  tab: PropTypes.number.isRequired,
};

export default injectStateService(injectIntl(MailTemplateOverview));
